import React, { useEffect, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import {
  maxWidthContainer,
  maxWidthContainerWithoutGutters,
} from '@noths/polaris-client-ribbons-base';
import { LazyRender } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { Tabs } from 'src/components/molecules/Tabs/Tabs';
import { PartnerLegals } from 'src/components/organisms/PartnerLegals/PartnerLegals';
import { PartnerListing } from 'src/components/organisms/PartnerListing/PartnerListing';
import { PartnerPageHeader } from 'src/components/organisms/PartnerPageHeader/PartnerPageHeader';
import { PartnerStory } from 'src/components/organisms/PartnerStory/PartnerStory';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarouselContainer/RecommendedProductsCarouselContainer';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { PartnerContentState } from 'src/redux/content/types';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import type { PartnerInsightsState } from 'src/redux/partnerInsights/types';
import * as styles from './PartnerLayout.styles';

export interface PartnerLayoutProps {
  pageLiveRegionText: string;
  partnerContent: PartnerContentState;
  partnerInsights?: PartnerInsightsState;
}

const LAZY_LOAD_MARGIN = '200px';

export const PartnerLayout = ({
  pageLiveRegionText,
  partnerContent,
  partnerInsights,
}: PartnerLayoutProps) => {
  const dispatch = useDispatch();
  const store = useStore<ReduxApplicationState>();

  // Sync hash to Redux when it changes (e.g. tab click) so pagination and URL updates preserve it.
  // Only dispatch when the hash actually changed to avoid redundant Redux updates and re-renders.
  useEffect(() => {
    const syncHash = () => {
      const hash = window.location.hash;
      if (store.getState().navigation.hash !== hash) {
        dispatch(navigationActions.setHash(hash));
      }
    };

    syncHash();
    window.addEventListener('hashchange', syncHash);

    return () => {
      window.removeEventListener('hashchange', syncHash);
    };
  }, [dispatch, store]);

  const tabs = useMemo(
    () => [
      {
        label: 'Our products',
        id: 'products',
        content: <PartnerListing stickyTopBanner={true} />,
      },
      {
        label: 'Our story',
        id: 'ourstory',
        content: <PartnerStory partnerContent={partnerContent} />,
      },
    ],
    [partnerContent],
  );

  return (
    <>
      {pageLiveRegionText && (
        <div css={visuallyHidden} role="status">
          {pageLiveRegionText}
        </div>
      )}
      <div css={styles.partnerPageHeaderWrapper}>
        <div css={maxWidthContainerWithoutGutters}>
          <PartnerPageHeader
            coverImageSrc={partnerContent.coverImage?.src ?? ''}
            introText={partnerContent.shortBrandBio ?? ''}
            location={partnerContent.location ?? ''}
            logoImageSrc={partnerContent.logo?.src ?? ''}
            name={partnerContent.name ?? ''}
            numberOfOrders={partnerInsights?.numberOfSales}
            rating={partnerInsights?.averageProductRating ?? undefined}
            startDate={partnerContent.startDate}
            topBrand={partnerInsights?.topBrand}
          />
        </div>
      </div>
      <div css={maxWidthContainer}>
        <div css={styles.carouselContainer}>
          <LazyRender
            options={{
              rootMargin: LAZY_LOAD_MARGIN,
            }}
          >
            <RecommendedProductsCarouselContainer
              placementType="item_page.same_seller2"
              title="Our bestsellers"
            />
          </LazyRender>
        </div>
      </div>
      <div css={maxWidthContainerWithoutGutters}>
        <Tabs ariaLabel="Partner sections" tabs={tabs} />
      </div>
      <div css={maxWidthContainerWithoutGutters}>
        <LazyRender
          options={{
            rootMargin: LAZY_LOAD_MARGIN,
          }}
        >
          <PartnerLegals shortcode={partnerContent.shortcode} />
        </LazyRender>
      </div>
    </>
  );
};
